import createCase from 'api/createCase';
import getDestinationQualifiers from 'api/destination/getDestinationQualifiers';
import { push } from 'connected-react-router';
import { call, put, select } from 'redux-saga/effects';
import applicationSpinnerActions from 'redux/actions/applicationSpinnerActions';
import caseCreationActions from 'redux/actions/caseCreationActions';
import destinationActions from 'redux/actions/destinationActions';
import { moveToNextStep } from 'redux/actions/stepActions';
import { addGtmDataLayer } from 'redux/actions/gtmActions';
import { hasQuestionsForAccident } from 'redux/sagas/step/step-saga-helper';
import {
  caseCreationSelector,
  decisionTreeResultSelector,
} from 'redux/selectors/caseCreation';
import {
  isDestinationFeatureEnabledSelector,
  mapToRenderInLocationSelector,
  partnerSelector,
  selectMoveVersion,
  selectPartnerReference,
  selectTenantId,
  shouldHideFirstAndLastNameInMotorContactDetailsSelector,
  showConfirmationFollowUpPageSelector,
  gtmV2Selector,
  shouldVerifyMaxCreatedCasesSelector,
  maxCreatedCasesAllowedSelector,
  selectParser,
} from 'redux/selectors/config';
import { routes } from 'Routes/routesConfig';
import { getSynergySessionToken } from 'utils/sagas-utils';
import {
  getAccidentData,
  getCause,
  getCustomerData,
  getLocationByMapProvider,
  getPlaceType,
  getServiceAppointment,
  getSpecialRequirements,
  getVehicleDetails,
  isDuplicateCase,
} from './utils/create-case-synergy-utils';
import { ga4Event } from 'analytics';
import parsers from './parsers';
import { updateTemporalLocation } from 'redux/actions/locationActions';

const caseCreationSuccess = 'successfully_digital_case_creation';

const { startLoad, stopLoad } = applicationSpinnerActions;
const { setDestinationQualifiers, setTowingServiceId } = destinationActions;

export function* clearSynergyCaseDataSaga() {
  // Ensuring cleanup for Destination
  yield put(
    updateTemporalLocation({
      address: undefined,
      coordinates: undefined,
    }),
  );
}

export function* getTowingService(result) {
  if (Array.isArray(result.services)) {
    const services = yield result.services.find(
      service => service && service.service_code === 'TOWING',
    ) || {};
    return services;
  }

  return {};
}

export function* hasDestinationFeature(status, towingService) {
  const isDestinationFeatureEnabled = yield select(
    isDestinationFeatureEnabledSelector,
  );

  const hasTowingService = towingService => towingService?.service_id;

  return (
    isDestinationFeatureEnabled &&
    [201, 202].includes(status) &&
    hasTowingService(towingService)
  );
}

function* getCaseData() {
  const parser = yield select(selectParser);
  const partnerReference = yield select(selectPartnerReference);
  const mapProvider = yield select(mapToRenderInLocationSelector);
  const decisionTreeResult = yield select(decisionTreeResultSelector);
  const verifyMaxCreatedCases = yield select(
    shouldVerifyMaxCreatedCasesSelector,
  );
  const maxCreatedCasesAllowed = yield select(maxCreatedCasesAllowedSelector);
  const hideFirstAndLastName = yield select(
    shouldHideFirstAndLastNameInMotorContactDetailsSelector,
  );
  const isAccident = hasQuestionsForAccident(decisionTreeResult);
  const {
    accident,
    currentLocation,
    isPolicyHolder,
    passengers,
    personName,
    personSurname,
    phoneCountryCodeContact,
    phoneNumberContact,
    vehicleDetails,
    serviceAppointment,
    additional_information,
    has_a_height_restriction,
    is_at_motorway,
    /**
     * For Synergy's mini create case journey
     *
     * We have a custom step asking for plate number
     * and it's being saved on `data`
     */
    plateNumber,
  } = yield select(caseCreationSelector);
  const payload = {
    cause: getCause(decisionTreeResult),
    vehicle: getVehicleDetails(plateNumber, vehicleDetails),
    customer: getCustomerData(
      hideFirstAndLastName,
      isPolicyHolder,
      personName,
      personSurname,
      phoneCountryCodeContact,
      phoneNumberContact,
    ),
    current_location: {
      ...getLocationByMapProvider(mapProvider, currentLocation),
      ...(has_a_height_restriction === null
        ? {}
        : { has_a_height_restriction }),
      ...(additional_information === '' ? {} : { additional_information }),
      place_type: getPlaceType(is_at_motorway),
    },
    partner_reference: partnerReference,
    passengers: {
      children_number: passengers?.childrenNumber || 0,
      passengers_number: passengers?.passengersNumber,
    },
    service_appointment: getServiceAppointment(serviceAppointment),
    ...getSpecialRequirements(passengers?.details),
    ...(isAccident ? getAccidentData(accident) : {}),
    maxCasesCreatedVerification: verifyMaxCreatedCases
      ? maxCreatedCasesAllowed
      : undefined,
  };

  const parserSelected = parsers[parser];

  return parserSelected ? parserSelected(payload) : payload;
}

function* handleDestinationFeature({
  caseReference,
  moveVersion,
  sessionToken,
  tenantId,
  towingService,
}) {
  const gtm_v2 = yield select(gtmV2Selector);
  yield put(
    addGtmDataLayer({
      is_destination_requested: 1,
    }),
  );
  yield put(setTowingServiceId({ serviceId: towingService.service_id }));

  const response = yield call(getDestinationQualifiers, {
    caseId: caseReference,
    serviceId: towingService.service_id,
    token: sessionToken,
    tenantId,
    moveVersion,
  });

  const { result: responseDestination, status: statusDestination } = response;

  if (statusDestination === 200) {
    yield put(
      setDestinationQualifiers({
        choices: responseDestination,
      }),
    );
    yield call(ga4Event, {
      eventaction: caseCreationSuccess,
      payload: {
        origin: 'destination',
      },
      gtm_v2,
    });
    yield put(moveToNextStep());
  } else {
    yield call(ga4Event, {
      eventaction: caseCreationSuccess,
      payload: {
        origin: 'call back',
      },
      gtm_v2,
    });
    yield put(push(routes.CALLBACK));
  }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function* createSynergyCaseSaga() {
  try {
    yield put(startLoad({}));

    const gtm_v2 = yield select(gtmV2Selector);
    const tenantId = yield select(selectTenantId);
    const moveVersion = yield select(selectMoveVersion);
    const partner = yield select(partnerSelector);

    const isSuccessCase = status => [200, 201, 202].includes(status);

    const caseData = yield getCaseData();

    yield put(caseCreationActions.clearSynergyCaseData());

    const sessionToken = yield getSynergySessionToken();

    const { result, status } = yield call(createCase, {
      body: caseData,
      token: sessionToken,
      tenantId,
      moveVersion,
      partner,
    });

    if (isSuccessCase(status)) {
      const shouldRedirectToConfirmationFollowUp = yield select(
        showConfirmationFollowUpPageSelector,
      );

      const { case_guid: caseGuid, case_id: caseReference } = result;

      yield put(
        caseCreationActions.createSynergyCaseSuccess({
          data: caseData,
          caseGuid,
          caseReference,
        }),
      );

      const towingService = yield getTowingService(result);
      const hasDestinationFeatureEnabled = yield hasDestinationFeature(
        status,
        towingService,
      );
      if (hasDestinationFeatureEnabled) {
        yield handleDestinationFeature({
          caseReference,
          moveVersion,
          sessionToken,
          tenantId,
          towingService,
        });
        return;
      }

      yield put(
        addGtmDataLayer({
          is_destination_requested: 0,
        }),
      );

      if (status === 201) {
        if (shouldRedirectToConfirmationFollowUp) {
          ga4Event({
            eventaction: caseCreationSuccess,
            payload: {
              origin: 'follow up',
            },
            gtm_v2,
          });
          yield put(push(routes.SYNERGY_FOLLOW_CONFIRMATION));
          return;
        }

        ga4Event({
          eventaction: caseCreationSuccess,
          payload: {
            origin: 'confirmation',
          },
          gtm_v2,
        });

        yield put(push(routes.CONFIRMATION));
      }

      ga4Event({
        eventaction: caseCreationSuccess,
        payload: {
          origin: 'call back',
        },
        gtm_v2,
      });
      yield put(push(routes.CALLBACK));
      return;
    }
    yield put(caseCreationActions.createCaseFail(result));
    yield put(
      push(
        isDuplicateCase(status)
          ? routes.CONTACT_US_DUPLICATE
          : routes.CONTACT_US_CASE_CREATION_FAIL,
      ),
    );
  } catch (error) {
    yield put(caseCreationActions.createCaseFail(error?.message || error));
    yield put(push(routes.CONTACT_US_CASE_CREATION_FAIL));
  } finally {
    yield put(stopLoad({}));
  }
}
